import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { api } from '../lib/api';
import { useAuthStore } from '../store/auth';

export default function AuthPage() {
  const [mode, setMode] = useState('login');
  const [form, setForm] = useState({ email: '', password: '', full_name: '', invite_code: '' });
  const [loading, setLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const navigate = useNavigate();
  const setAuth = useAuthStore(s => s.setAuth);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (mode === 'register' && form.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    setLoading(true);
    try {
      const res = mode === 'login'
        ? await api.login({ email: form.email, password: form.password })
        : await api.register(form);
      setAuth({ email: res.email, full_name: res.full_name, id: res.user_id }, res.access_token);
      toast.success(mode === 'login' ? 'Welcome back!' : 'Account created!');
      // Force full reload to ensure localStorage is flushed on all browsers (esp. iOS Safari)
      window.location.href = '/';
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  return (
    <div className="auth-page">
      <div className="auth-card animate-in">
        <div className="auth-logo">Arca</div>
        <div className="auth-subtitle">Personal finance, finally intelligent</div>

        <div className="tabs" style={{ margin: '0 auto 28px', display: 'flex' }}>
          <button className={`tab ${mode === 'login' ? 'active' : ''}`} onClick={() => setMode('login')}>Sign In</button>
          <button className={`tab ${mode === 'register' ? 'active' : ''}`} onClick={() => setMode('register')}>Create Account</button>
        </div>

        <form onSubmit={handleSubmit}>
          {mode === 'register' && (
            <>
              <div className="form-group">
                <label className="form-label">Full Name</label>
                <input className="input" type="text" placeholder="Your name" value={form.full_name} onChange={set('full_name')} required />
              </div>
              <div className="form-group">
                <label className="form-label">Invite Code</label>
                <input className="input" type="text" placeholder="Ask the app owner for this" value={form.invite_code} onChange={set('invite_code')} />
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>Required to create an account</div>
              </div>
            </>
          )}

          <div className="form-group">
            <label className="form-label">Email</label>
            <input className="input" type="email" placeholder="you@example.com" value={form.email} onChange={set('email')} required />
          </div>

          <div className="form-group" style={{ marginBottom: 24 }}>
            <label className="form-label">Password</label>
            <div style={{ position: 'relative' }}>
              <input
                className="input"
                type={showPass ? 'text' : 'password'}
                placeholder="Min 8 characters"
                value={form.password}
                onChange={set('password')}
                required
                minLength={8}
                style={{ paddingRight: 44 }}
              />
              <button
                type="button"
                onClick={() => setShowPass(s => !s)}
                style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 14 }}
              >
                {showPass ? '' : ''}
              </button>
            </div>
            {mode === 'register' && form.password.length > 0 && (
              <PasswordStrength password={form.password} />
            )}
          </div>

          <button
            className="btn btn-primary"
            style={{ width: '100%', justifyContent: 'center', padding: '11px' }}
            disabled={loading}
          >
            {loading ? 'Loading…' : mode === 'login' ? 'Sign In' : 'Create Account'}
          </button>
        </form>

        {mode === 'login' && (
          <div style={{ textAlign: 'center', marginTop: 20, fontSize: 12, color: 'var(--text-muted)' }}>
            After 5 wrong attempts your IP will be locked out for 15 minutes.
          </div>
        )}
      </div>
    </div>
  );
}

function PasswordStrength({ password }) {
  const checks = [
    { label: '8+ characters', pass: password.length >= 8 },
    { label: 'Has number', pass: /\d/.test(password) },
    { label: 'Has uppercase', pass: /[A-Z]/.test(password) },
    { label: 'Has symbol', pass: /[^a-zA-Z0-9]/.test(password) },
  ];
  const score = checks.filter(c => c.pass).length;
  const colors = ['var(--red)', 'var(--red)', '#eab308', 'var(--green)', 'var(--green)'];
  const labels = ['', 'Weak', 'Fair', 'Good', 'Strong'];

  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ display: 'flex', gap: 4, marginBottom: 6 }}>
        {[1,2,3,4].map(i => (
          <div key={i} style={{ flex: 1, height: 3, borderRadius: 99, background: i <= score ? colors[score] : 'var(--bg-elevated)', transition: 'background 0.2s' }} />
        ))}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: 10 }}>
          {checks.map(c => (
            <span key={c.label} style={{ fontSize: 10, color: c.pass ? 'var(--green)' : 'var(--text-muted)' }}>
              {c.pass ? '✓' : '·'} {c.label}
            </span>
          ))}
        </div>
        <span style={{ fontSize: 11, color: colors[score], fontWeight: 600 }}>{labels[score]}</span>
      </div>
    </div>
  );
}
